import React from "react";
import LegalLayout, { Section } from "./LegalLayout";

export default function Terms() {
  return (
    <div data-testid="terms-page">
      <LegalLayout title="Terms & Conditions" lastUpdated="February 2026">
        <p>
          These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your use of the JFeva Solutions, LLC website and
          the flooring and tile installation services we provide (collectively, the &ldquo;Services&rdquo;). By using
          our site, requesting an estimate, or hiring us for a project, you agree to these Terms.
        </p>

        <Section title="1. Services" testid="terms-services">
          <p>
            JFeva Solutions, LLC provides flooring installation (laminate, luxury vinyl, hardwood, tile), tile installation
            (bathrooms, kitchens, backsplashes, showers), repairs and replacements, and custom design work in Florida
            and surrounding areas. Specific work scope, materials, and pricing for your project will be detailed in a
            written estimate.
          </p>
        </Section>

        <Section title="2. Estimates & Quotes" testid="terms-estimates">
          <ul className="list-disc pl-6 space-y-1">
            <li>Estimates are provided in good faith and are valid for 30 days from the date issued.</li>
            <li>Estimates are based on the information and site conditions available to us. If conditions discovered after work begins require additional labor or materials (e.g., sub-floor rot, hidden damage, out-of-level framing), we will contact you with a written change order before proceeding.</li>
            <li>Material prices can change based on supplier availability; we will match prices to the final estimate at the time of material purchase and notify you of any variance.</li>
          </ul>
        </Section>

        <Section title="3. Scheduling & Access" testid="terms-schedule">
          <ul className="list-disc pl-6 space-y-1">
            <li>Installation dates are confirmed once a deposit is received and materials are available.</li>
            <li>You agree to provide safe, clean access to the work area, including water, electricity, and a space to stage materials.</li>
            <li>If a scheduled day cannot proceed due to inaccessibility, pets uncontained in the work area, or other on-site conditions beyond our control, additional charges may apply for the lost day.</li>
          </ul>
        </Section>

        <Section title="4. Payment Terms" testid="terms-payment">
          <ul className="list-disc pl-6 space-y-1">
            <li>Projects over $1,000 typically require a deposit at acceptance (usually 30–50%), a progress payment at a defined milestone, and a final balance within 7 days of project completion.</li>
            <li>Payment methods accepted: check, bank transfer, major credit cards, and digital invoice payments.</li>
            <li>Late balances accrue interest at the lesser of 1.5% per month or the maximum rate allowed by Florida law.</li>
          </ul>
        </Section>

        <Section title="5. Warranty on Workmanship" testid="terms-warranty">
          <p>
            JFeva Solutions, LLC warrants its installation workmanship for one (1) year from the date of substantial
            completion. Manufacturer product warranties pass through directly from the manufacturer to you. This workmanship
            warranty does not cover: normal wear and tear, damage from water intrusion or structural movement, use outside
            the manufacturer&apos;s recommended conditions, or alterations/repairs performed by anyone other than us.
          </p>
          <p>
            To claim under this warranty, email <a className="underline hover:text-[color:var(--brand-ink)]" href="mailto:info@jfeva.com">info@jfeva.com</a> with
            project address, photos of the issue, and a description. We will inspect within a reasonable time and repair
            qualifying defects at no cost.
          </p>
        </Section>

        <Section title="6. Customer Responsibilities" testid="terms-customer">
          <ul className="list-disc pl-6 space-y-1">
            <li>Remove or safeguard valuables and fragile items from the work area before we arrive.</li>
            <li>Verify material selections (color, finish, size) at proofing — once a cut tile or opened box is installed, it cannot be returned.</li>
            <li>Keep children and pets out of active work areas.</li>
          </ul>
        </Section>

        <Section title="7. Limitation of Liability" testid="terms-liability">
          <p>
            To the maximum extent allowed by law, JFeva Solutions, LLC&apos;s total liability under any claim related to
            the Services is limited to the total amount you paid for the specific project giving rise to the claim. We
            are not liable for incidental, consequential, or punitive damages, including lost use, loss of business, or
            emotional distress.
          </p>
        </Section>

        <Section title="8. Cancellation" testid="terms-cancel">
          <p>
            You may cancel a scheduled project any time before materials are ordered without penalty. After materials
            are ordered or special-order items are purchased, you remain responsible for non-returnable materials and
            any restocking fees. See our <a className="underline hover:text-[color:var(--brand-ink)]" href="/refund">Refund Policy</a> for details.
          </p>
        </Section>

        <Section title="9. Governing Law" testid="terms-law">
          <p>
            These Terms are governed by the laws of the State of Florida without regard to conflict-of-law principles.
            Any dispute arising from these Terms will be resolved in the state or federal courts located in Florida.
          </p>
        </Section>

        <Section title="10. Changes to These Terms" testid="terms-changes">
          <p>
            We may update these Terms periodically. Continued use of our Services or site after an update constitutes
            acceptance. The &ldquo;Last updated&rdquo; date above reflects the latest revision.
          </p>
        </Section>

        <Section title="11. Contact" testid="terms-contact">
          <p>
            JFeva Solutions, LLC — <a className="underline hover:text-[color:var(--brand-ink)]" href="mailto:info@jfeva.com">info@jfeva.com</a> · <a className="underline hover:text-[color:var(--brand-ink)]" href="tel:8502520162">850-252-0162</a>
          </p>
        </Section>
      </LegalLayout>
    </div>
  );
}
